import { createFileRoute } from '@tanstack/react-router';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Suspense } from 'react';
import { trpc } from '@/lib/trpc';
import { Header } from './-components/header';
import { Footer } from './-components/footer';
import { Layout } from './-components/layout';
import { Hero } from './-components/home/hero';
import { Solutions } from './-components/home/solutions';
import { Marquee } from './-components/home/marquee';
import { Partners } from './-components/home/partners';
import { RecentProjects } from './-components/home/recent-projects';
import { MoreAbout } from './-components/home/more-about';
import { AppDownload } from './-components/home/app-download';

export const Route = createFileRoute('/new/')({
	loader: ({ context }) => {
		// Prefetch the global stats (also carries the collaborating-NGO logos) and the recent projects.
		// `prefetchQuery`, not `ensureQueryData`: these are fire-and-forget warm-ups for the
		// `useSuspenseQuery` calls below, and `prefetchQuery` never rejects. With `ensureQueryData` the
		// returned promise is left floating, so when a visitor navigates away or reloads before these
		// slow queries settle, the SSR teardown cancels them, the floating promise rejects with
		// `CancelledError` and — with nothing attached to it — takes the whole server process down.
		context.queryClient.prefetchQuery(trpc.portal.getWorldcooGlobalStats.queryOptions());
		context.queryClient.prefetchQuery(trpc.portal.getRecentProjects.queryOptions({ locale: context.locale }));
	},
	component: Component,
});

/**
 * Real partner-merchant logos from getWorldcooGlobalStats → partnerMerchants: every merchant with a
 * logo and a live campaign, ordered by amount raised (biggest first), uncapped upstream.
 */
function PartnersSection() {
	const { data: stats } = useSuspenseQuery(trpc.portal.getWorldcooGlobalStats.queryOptions());
	return <Partners merchants={stats.partnerMerchants} />;
}

/** Real recent projects from our system (getRecentProjects), localized to the current locale. */
function RecentProjectsSection() {
	const { locale } = Route.useRouteContext();
	const { data } = useSuspenseQuery(trpc.portal.getRecentProjects.queryOptions({ locale }));
	// Real partner-NGO count for the section copy (already prefetched in the loader, no waterfall).
	const { data: stats } = useSuspenseQuery(trpc.portal.getWorldcooGlobalStats.queryOptions());
	return <RecentProjects projects={data.projects} ngoCount={stats.socialOrganizations} />;
}

function Component() {
	return (
		<Layout>
			<Header />
			<Hero />
			<Solutions />
			<Marquee />
			<Suspense fallback={<div className="min-h-[600px]" />}>
				<PartnersSection />
			</Suspense>
			<Suspense fallback={<div className="min-h-[600px]" />}>
				<RecentProjectsSection />
			</Suspense>
			<MoreAbout />
			<AppDownload />
			<Footer />
		</Layout>
	);
}
